/* global PowerPoint console Office OfficeExtension */

import exists from "../../helpers/exists";
import { convertHorizontalAlign, convertOutline, convertPtToNumber, convertVerticalAlign } from "./bakingHelpers";

export async function draw(id, drawingCommands, left, top) {
  // Execute on drawing data
  try {
    const PX_TO_POINTS = exists("Office") ? (Office.context.platform === Office.PlatformType.Mac ? 1 : 0.75) : 0.75;
    const ALWAYS_PX_TO_POINTS = 0.75;
    const shapes = {};
    OfficeExtension.config.extendedErrorLogging = true;

    await PowerPoint.run(async (context) => {
      const slide = context.presentation.slides.getItem(id.slideId);

      drawingCommands.forEach((cmd) => {
        if (cmd.width < 0) {
          console.error("Invalid drawing command: width can not be negative!", cmd);
          return;
        }
        if (cmd.height < 0) {
          console.error("Invalid drawing command: height can not be negative!", cmd);
          return;
        }
        let shape;
        switch (cmd.type) {
          case "text": {
            shape = slide.shapes.addTextBox(cmd.text, {
              left: (cmd.left - left) * PX_TO_POINTS + left, // correcting for DPI ratio
              top: (cmd.top - top) * PX_TO_POINTS + top, // correcting for DPI ratio
              width: cmd.width * PX_TO_POINTS,
              height: cmd.height * PX_TO_POINTS,
            });
            shape.textFrame.leftMargin =
              shape.textFrame.rightMargin =
              shape.textFrame.topMargin =
              shape.textFrame.bottomMargin =
                0;
            // shape.textFrame.leftMargin = cmd.margins.left * PX_TO_POINTS;
            // shape.textFrame.rightMargin = cmd.margins.right * PX_TO_POINTS;
            // shape.textFrame.topMargin = cmd.margins.top * PX_TO_POINTS;
            // shape.textFrame.bottomMargin = cmd.margins.bottom * PX_TO_POINTS;

            shape.textFrame.textRange.text = cmd.text;
            if (cmd.fontFamily) shape.textFrame.textRange.font.name = cmd.fontFamily;
            if (cmd.fontSize) shape.textFrame.textRange.font.size = convertPtToNumber(cmd.fontSize);
            shape.textFrame.textRange.font.color = cmd.color ?? "#000000";
            shape.textFrame.textRange.font.bold = cmd.bold;
            shape.textFrame.textRange.font.italic = cmd.italic;
            shape.textFrame.textRange.font.underline = cmd.underline ? "Single" : "None";
            shape.textFrame.textRange.paragraphFormat.horizontalAlignment = convertHorizontalAlign(cmd.horizontalAlign);
            shape.textFrame.verticalAlignment = convertVerticalAlign(cmd.verticalAlign);
            shape.textFrame.autoSizeSetting = "AutoSizeNone";

            // these spacings are not available through PPT JS API:
            shape.textFrame.textRange.paragraphFormat.lineSpacing = cmd.spacing.line;
            shape.textFrame.textRange.paragraphFormat.spaceAfter = cmd.spacing.paragraph;

            //shape.textFrame.textRange.getSubstring(0, 1).font.bold = true;
            if (cmd.subStrings && cmd.subStrings.length > 0) {
              for (let i = 0; i < cmd.subStrings.length; i++) {
                const cs = cmd.subStrings[i];
                const substring = shape.textFrame.textRange.getSubstring(cs.from, cs.length);
                substring.font.bold = cs.bold ?? false;
                substring.font.italic = cs.italic ?? false;
                substring.font.underline = cs.underline ? "Single" : "None";
                if (cs.bullet || cs.numbered) {
                  substring.paragraphFormat.bulletFormat.visible = true;
                  // TODO: seems setting does not work as of today's version 1.10, only in Preview
                  // substring.paragraphFormat.bulletFormat.style = cs.numbered
                  //   ? "ArabicNumeralPeriod"
                  //   : "AlphabetLowercasePeriod";
                }
              }
            }
            break;
          }
          case "rect":
          case "ellipse":
          case "chevron":
          case "thinchevron":
          case "triangle":
          case "trapezoid":
          case "arrow":
          case "circularArrow":
          case "quarter": {
            const geometricShape = getGeometricShape(cmd);
            if (!geometricShape) throw new Error("Unrecognized geometric shape", cmd);
            shape = slide.shapes.addGeometricShape(geometricShape, {
              left: (cmd.left - left) * PX_TO_POINTS + left, // correcting for DPI ratio
              top: (cmd.top - top) * PX_TO_POINTS + top, // correcting for DPI ratio
              width: cmd.width * PX_TO_POINTS,
              height: cmd.height * PX_TO_POINTS,
            });
            if (cmd.angle) shape.adjustments.set(getAngleAdjustmentIndex(cmd), cmd.angle);
            if (cmd.angleB) shape.adjustments.set(1, cmd.angleB);
            if (cmd.radius) shape.radius = cmd.radius;
            if (cmd.rotation && cmd.rotation !== 0) shape.rotation = cmd.rotation;

            if (cmd.outline && cmd.outline.color !== "transparent" && cmd.outline.color !== "none") {
              if (cmd.outline.color) shape.lineFormat.color = cmd.outline.color;
              if (cmd.outline.weight) shape.lineFormat.weight = cmd.outline.weight * ALWAYS_PX_TO_POINTS; // outlines are always correctly rendered on Mac and Win in pt so it has to be converted
              if (cmd.outline.style) shape.lineFormat.dashStyle = getDashStyle(cmd);
            } else shape.lineFormat.visible = false;

            shape.fill.foregroundColor =
              cmd.fill !== "none" && cmd.fill !== "transparent" ? (cmd.fill ?? "#FFFFFF") : "#FFFFFF";
            shape.fill.transparency = cmd.transparency ?? 0;
            shape.textFrame.textRange.font.color = cmd.fontColor ?? "#000000";
            break;
          }
          case "border": {
            shape = slide.shapes.addLine(PowerPoint.ConnectorType.straight, {
              left: (cmd.left - left) * PX_TO_POINTS + left, // correcting for DPI ratio
              top: (cmd.top - top) * PX_TO_POINTS + top, // correcting for DPI ratio
              width: cmd.width * PX_TO_POINTS,
              height: cmd.height * PX_TO_POINTS,
            });
            shape.lineFormat.color = cmd.color;
            shape.lineFormat.weight = cmd.weight * ALWAYS_PX_TO_POINTS; // outlines are always correctly rendered on Mac and Win in pt so it has to be converted
            shape.lineFormat.dashStyle = convertOutline(cmd.style);
            break;
          }
          case "image": {
            // deferred
            break;
          }
          case "svg": {
            // deferred
            break;
          }
          case "scale": {
            // deferred
            break;
          }
          default:
            console.warn("Unrecognized drawing command", cmd);
            return;
        }
        if (shape) {
          shape.name = "Gridd_" + cmd.type + "_" + cmd.order + (cmd.tag ? "_" + cmd.tag : "");
          shapes[cmd.tag] = shape;
          console.log("Rendered shape " + shape.name, { cmd, shape });
        }
      });

      await context.sync();

      // const shapeTags = Object.keys(shapes);
      // for (let i = 0; i < shapeTags.length; i++) {
      //   const shape = shapes[shapeTags[i]];
      //   shape.load({ adjustments: true, id: true, type: true });
      //   await context.sync();
      //   if (shape.adjustments.count > 0) {
      //     for (let j = 0; j < shape.adjustments.count; j++) {
      //       const adj = shape.adjustments.get(j);
      //       console.log("Shape adjustment value", { tag: shapeTags[i], j, shape, adj });
      //     }
      //     //console.log("Shape adjustments if there is any", { adjs: shape.adjustments, id: shape.id, type: shape.type });
      //   }
      // }
    });

    drawingCommands.forEach((cmd) => {
      switch (cmd.type) {
        case "image": {
          Office.context.document.setSelectedDataAsync(
            cmd.data,
            {
              coercionType: Office.CoercionType.Image,
              imageLeft: (cmd.left - left) * PX_TO_POINTS + left, // correcting for DPI ratio
              imageTop: (cmd.top - top) * PX_TO_POINTS + top, // correcting for DPI ratio
              imageWidth: cmd.width * PX_TO_POINTS,
              imageHeight: cmd.height * PX_TO_POINTS,
            },
            (r) => {
              console.log("Image insert result", { result: r, cmd });
            }
          );
          break;
        }
        case "svg": {
          try {
            Office.context.document.setSelectedDataAsync(
              cmd.data,
              {
                coercionType: Office.CoercionType.XmlSvg,
                imageLeft: (cmd.left - left) * PX_TO_POINTS + left, // correcting for DPI ratio
                imageTop: (cmd.top - top) * PX_TO_POINTS + top, // correcting for DPI ratio
                imageWidth: cmd.width * PX_TO_POINTS,
                imageHeight: cmd.height * PX_TO_POINTS,
              },
              (r) => {
                console.log("SVG insert result", { result: r });
              }
            );
          } catch (error) {
            console.warn("Error executing SVG command", { error, cmd });
          }
          break;
        }
      }
    });
  } catch (error) {
    console.error("Error during drawing into PPT", error);
  }
}

function getGeometricShape(cmd) {
  switch (cmd.type) {
    case "rect":
      return cmd.radius === 0 ? PowerPoint.GeometricShapeType.rectangle : PowerPoint.GeometricShapeType.roundRectangle;
    case "ellipse":
      return PowerPoint.GeometricShapeType.ellipse;
    case "chevron":
      return PowerPoint.GeometricShapeType.homePlate;
    case "triangle":
      return PowerPoint.GeometricShapeType.triangle;
    case "trapezoid":
      return PowerPoint.GeometricShapeType.trapezoid;
    case "arrow":
      return PowerPoint.GeometricShapeType.rightArrow;
    case "circularArrow":
      return PowerPoint.GeometricShapeType.circularArrow;
    case "quarter":
      return PowerPoint.GeometricShapeType.pie;
    case "thinchevron":
      return PowerPoint.GeometricShapeType.chevron;
  }
  return undefined;
}

function getAngleAdjustmentIndex(cmd) {
  switch (cmd.type) {
    case "circularArrow":
      return 3;
    case "quarter":
      return 0;
    case "trapezoid":
      return 0;
    case "chevron":
      return 0;
    case "thinchevron":
      return 0;
  }
  return undefined;
}

function getDashStyle(cmd) {
  switch (cmd.outline.style) {
    case "dotted":
      return PowerPoint.ShapeLineDashStyle.roundDot;
    case "dashed":
      return PowerPoint.ShapeLineDashStyle.dash;
    default:
      return PowerPoint.ShapeLineDashStyle.solid;
  }
}
